import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import Notification from "../../components/Notification/Notification";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import "./LGABudgetDetail.css";

const LGABudgetDetail = () => {
    const location = useLocation();
    const budgetId = location?.state?.budgetId;
    const navigate = useNavigate();

    const [budget, setBudget] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState("");

    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
    const [confirmDialog, setConfirmDialog] = useState({ show: false, token: "", action: null, label: "" });

    useEffect(() => {
        const fetchBudgetDetails = async () => {
            try {
                setLoading(true);
                const [budgetRes, summaryRes] = await Promise.all([
                    privateApi.get(`/api/lga/budgets/${budgetId}`),
                    privateApi.get(`/api/budgets/${budgetId}/lines-summary`)
                ]);
                setBudget(budgetRes.data.data);
                setSummary(summaryRes.data.data);
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load LGA budget.");
            } finally {
                setLoading(false);
            }
        };

        if (budgetId) fetchBudgetDetails();
    }, [budgetId]);

    const showNotification = (message, type = "success") => setNotification({ show: true, message, type });
    const closeNotification = () => setNotification(prev => ({ ...prev, show: false }));

    // Confirm Dialog
    const handleActionClick = (actionType) => {
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setConfirmDialog({
            show: true,
            token,
            action: actionType,
            label: actionType === "approve" ? "Approve this LGA budget" : "Lock this LGA budget"
        });
    };

    const handleConfirmDialog = async (input) => {
        if (input !== confirmDialog.token) {
            showNotification("Confirmation code does not match.", "error");
            return;
        }
        setConfirmDialog(prev => ({ ...prev, show: false }));

        if (confirmDialog.action === "approve") await approveBudget();
        if (confirmDialog.action === "lock") await lockBudget();
    };

    const handleCancelDialog = () => setConfirmDialog(prev => ({ ...prev, show: false }));

    const approveBudget = async () => {
        try {
            setActionLoading(true);
            const { data } = await privateApi.patch(`/api/lga/budgets/${budgetId}/approve`);
            setBudget(data.data);
            showNotification("LGA budget approved successfully.");
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to approve budget.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const lockBudget = async () => {
        try {
            setActionLoading(true);
            const { data } = await privateApi.patch(`/api/lga/budgets/${budgetId}/lock`);
            setBudget(data.data);
            showNotification("LGA budget locked successfully.");
        } catch (err) {
            showNotification(err.response?.data?.message || "Failed to lock budget.", "error");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <Spinner loadingInfo="Loading LGA Budget Details..." />;
    if (error) return <p className="lga-budget-detail-error">{error}</p>;
    if (!budget) return <p>No budget found.</p>;

    return (
        <div className="lga-budget-detail-container">

            <Notification show={notification.show} message={notification.message} type={notification.type} onClose={closeNotification} />

            <ConfirmDialog
                show={confirmDialog.show}
                token={confirmDialog.token}
                action={confirmDialog.label}
                onConfirm={handleConfirmDialog}
                onCancel={handleCancelDialog}
            />

            <header className="lga-budget-detail-header">
                <h2>LGA Budget Details</h2>
            </header>

            <div className="lga-budget-detail-card">

                <div className="detail-row"><span>LGA:</span><strong>{budget.lgaId?.name || "-"}</strong></div>
                <div className="detail-row"><span>Year:</span><strong>{budget.year}</strong></div>
                <div className="detail-row"><span>Type:</span><strong>{budget.type}</strong></div>
                <div className="detail-row"><span>Revision Number:</span><strong>{budget.revisionNumber}</strong></div>
                <div className="detail-row"><span>Status:</span><strong className={`status ${budget.status}`}>{budget.status}</strong></div>
                <div className="detail-row"><span>Remark:</span><strong>{budget.remark || "-"}</strong></div>
                <div className="detail-row"><span>Created By:</span><strong>{budget.createdBy?.name || "-"}</strong></div>
                <div className="detail-row"><span>Approved By:</span><strong>{budget.approvedBy?.name || "-"}</strong></div>
                <div className="detail-row"><span>Locked By:</span><strong>{budget.lockedBy?.name || "-"}</strong></div>
                <div className="detail-row"><span>Created At:</span><strong>{new Date(budget.createdAt).toLocaleString()}</strong></div>

                {budget.approvedAt && <div className="detail-row"><span>Approved At:</span><strong>{new Date(budget.approvedAt).toLocaleString()}</strong></div>}
                {budget.lockedAt && <div className="detail-row"><span>Locked At:</span><strong>{new Date(budget.lockedAt).toLocaleString()}</strong></div>}

                {/* Budget Summary */}
                {summary && (
                    <div className="budget-summary-section">
                        <h3>Budget Lines Summary</h3>
                        <div className="summary-row"><span>Total Budgeted:</span><strong>{summary.totalBudgeted?.toLocaleString()}</strong></div>
                        <div className="summary-row"><span>Total Spent:</span><strong>{summary.totalSpent?.toLocaleString()}</strong></div>
                        <div className="summary-row remaining"><span>Remaining Amount:</span><strong>{summary.remainingAmount?.toLocaleString()}</strong></div>
                    </div>
                )}

                <div className="lga-budget-detail-actions">
                    {budget.status === "draft" && <button className="btn-approve" onClick={() => handleActionClick("approve")} disabled={actionLoading}>{actionLoading ? "Processing..." : "Approve Budget"}</button>}
                    {budget.status === "approved" && <button className="btn-lock" onClick={() => handleActionClick("lock")} disabled={actionLoading}>{actionLoading ? "Processing..." : "Lock Budget"}</button>}
                    {budget.budgetLineCount > 0 && <Link className="btn-lock" to={"/budget/budget-lines"} state={{ budget }}>View Budget Lines</Link>}
                    {budget.status === "draft" && <Link className="btn-lock" to={"/budget-lines/create"} state={{ budget }}>Create Budget Line</Link>}
                    <button className="back-btn" onClick={() => navigate(-1)}>Back</button>
                </div>

            </div>

        </div>
    );
};

export default LGABudgetDetail;